import React, { useCallback } from "react"
import { useQueryClient } from "react-query"
import { ActionCableConsumer } from "react-actioncable-provider"
import { useReactions } from "@hooks/useReactions"
import UserAvatar from "@components/shared/Header/UserAvatar"
import { SpinnerSmall } from "@components/shared/Spinner"
import Pluralize from "react-pluralize"
import { User } from "@models/bands"

interface Props {
  postId: number
  renderMode?: "full" | "text"
  isPastEvent?: boolean
}

interface CableMessage {
  reaction?: {
    reaction_type?: string
  }
}

const RSVP_REACTION_TYPES = new Set(["interested", "uninterested"])

const GoingBlock = ({ postId, renderMode = "full", isPastEvent = false }: Props) => {
  const queryClient = useQueryClient()
  const { isLoading, data } = useReactions("Post", postId, "rsvps", false)

  const handleReceivedMessage = useCallback(
    (msg: CableMessage) => {
      if (
        msg.reaction &&
        RSVP_REACTION_TYPES.has(msg.reaction.reaction_type ?? "")
      ) {
        queryClient.invalidateQueries([
          "reactions",
          "Post",
          postId,
          "rsvps",
          false,
        ])
      }
    },
    [postId, queryClient],
  )

  const count = data?.users?.length ?? 0

  return (
    <ActionCableConsumer
      channel={{ channel: "PostChannel", id: postId }}
      onReceived={handleReceivedMessage}
    >
      {isLoading ? (
        <SpinnerSmall className="ml-2" />
      ) : count === 0 ? null : renderMode === "text" ? (
        <span className="text-sm text-gray-600 ml-2">
          <Pluralize
            singular="person"
            plural="people"
            count={count}
            className="text-black-800 font-semibold"
          />{" "}
          going
        </span>
      ) : (
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">
            <Pluralize singular="person" plural="people" count={count} />{" "}
            {isPastEvent ? "went" : "going"}
          </p>
          <ul className="flex flex-wrap gap-2">
            {data.users.map((user: User) => (
              <li key={user.id}>
                <UserAvatar
                  handleClick={() => {}}
                  size="small"
                  user={user}
                  inHeader={false}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </ActionCableConsumer>
  )
}

export default GoingBlock
